import type { ColDef } from 'ag-grid-community';

import { useGetRecipProcHistoryQuery } from '@/app/services/inClinic';
import { DataGrid } from '@/components/common';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import type { RecipProcedure } from '../../types';
import { countHeatsNotUsed } from './heatsNotUsedCount';

interface RecipProcHistoryProps {
  recipId: string;
}

const colDefs: ColDef<RecipProcedure>[] = [
  {
    field: 'date',
    headerName: 'Date',
    width: 105,
    valueFormatter: (params) => {
      if (!params.value || params.value === '0001-01-01T00:00:00') return '';
      const d = new Date(params.value);
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
    },
  },
  { field: 'procCode', headerName: 'Proc Code', width: 130 },
  { field: 'description', headerName: 'Description', flex: 1, minWidth: 180 },
  { field: 'preg', headerName: 'Preg', width: 75 },
  { field: 'drugName', headerName: 'Drug', width: 120 },
  { field: 'timeAMPM', headerName: 'Time', width: 75 },
];

export function RecipProcHistory({ recipId }: RecipProcHistoryProps) {
  const { data: procedures = [], isLoading } = useGetRecipProcHistoryQuery(
    recipId,
    { skip: !recipId },
  );

  const showHeatsNotUsed = useFeatureFlag('Count_Heat_Not_Used');
  const heatsNotUsedCount = countHeatsNotUsed(procedures);

  return (
    <div className="ring-border flex flex-col overflow-hidden rounded-lg ring-1">
      <div className="bg-muted/30 flex items-center justify-between border-b px-3 py-1.5">
        <h2 className="text-xs font-semibold tracking-wide uppercase">
          Procedure History
        </h2>
        <div className="flex items-center gap-3">
          {showHeatsNotUsed && (
            <span className="text-muted-foreground text-xs">
              Number of Heats Not Used:{' '}
              <span className="font-semibold">{heatsNotUsedCount}</span>
            </span>
          )}
          <span className="text-muted-foreground text-xs">
            {procedures.length} procedures
          </span>
        </div>
      </div>
      <DataGrid<RecipProcedure>
        containerClassName="ag-theme-alpine ag-compact h-[260px] w-full p-2"
        rowData={procedures}
        columnDefs={colDefs}
        loading={isLoading}
        overlayNoRowsTemplate="No procedure history"
      />
    </div>
  );
}
